import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Card, Label, Input } from "@/components/shared";
import { Mail, ArrowLeft, ArrowRight, HelpCircle, Check, X, UserRound, HardHat } from "lucide-react";
import { HowItWorksModal } from "@/components/HowItWorks";
import { LanguageToggle } from "@/components/LanguageToggle";

const HERO = "https://images.unsplash.com/photo-1591588582259-e675bd2e6088?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const ROLES = {
  client: { label: "Client", Icon: UserRound, desc: "Request & track property-preservation services" },
  contractor: { label: "Contractor", Icon: HardHat, desc: "Get assigned jobs, submit bids & upload photos" },
};

export default function Login() {
  const navigate = useNavigate();
  const { applyAuth } = useAuth();
  const [mode, setMode] = useState(() => (new URLSearchParams(window.location.search).get("mode") === "reset" ? "reset" : "login")); // login | register | forgot | reset
  const [role, setRole] = useState("client");
  const [roleChosen, setRoleChosen] = useState(false);
  const [form, setForm] = useState(() => { const q = new URLSearchParams(window.location.search); return { name: "", email: q.get("email") || "", password: "", code: q.get("code") || "" }; });
  const [loading, setLoading] = useState(false);
  const [howOpen, setHowOpen] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pwChecks = [
    ["8+ characters", (form.password || "").length >= 8],
    ["Uppercase", /[A-Z]/.test(form.password)],
    ["Lowercase", /[a-z]/.test(form.password)],
    ["Number", /\d/.test(form.password)],
    ["Symbol", /[^A-Za-z0-9]/.test(form.password)],
  ];
  const pwStrong = pwChecks.every(([, ok]) => ok);

  const roleFromUser = (u) => (u.role === "crew" ? "contractor" : u.role); // admin|client|contractor

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        applyAuth(data.token, data.user);
        toast.success(`Welcome back, ${data.user.name}`);
        const actual = roleFromUser(data.user);
        if (data.user.role !== "admin" && actual !== role) {
          toast.info(`You're signed in as a ${ROLES[actual]?.label || actual}`);
        }
        navigate("/");
      } else if (mode === "register") {
        const { data } = await api.post("/auth/register", { name: form.name, email: form.email, password: form.password, role });
        applyAuth(data.token, data.user);
        toast.success(`Account created — welcome, ${data.user.name}`);
        navigate("/");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("If that email exists, a 6-digit code was sent");
        setMode("reset");
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { email: form.email, code: form.code, password: form.password });
        toast.success("Password reset — you can log in now");
        setMode("login");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    sessionStorage.setItem("ss_intended_role", role); // carried across the OAuth redirect; applied only on first-time account creation
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const fillDemo = (email, password) => { setForm({ ...form, email, password }); setMode("login"); setRole("client"); setRoleChosen(true); };

  const needPicker = (mode === "login" || mode === "register") && !roleChosen;
  const RoleIcon = ROLES[role].Icon;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#09090b]">
      {/* Left / hero */}
      <div className="relative hidden lg:block border-r-2 border-zinc-800">
        <img src={HERO} alt="Field services" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/60 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <img src="/sss-logo.png" alt="Swift Signings & Services LLC" className="w-16 h-16 border-2 border-black shadow-[4px_4px_0px_#000] mb-6 object-cover" />
          <h1 className="font-head font-black uppercase text-white text-5xl leading-[0.95] tracking-tight">
            Swift Signings<br />& Services <span className="text-[#FF5722]">LLC</span>
          </h1>
          <p className="text-zinc-400 mt-4 max-w-md">Property preservation & field-services dispatch. Jobs, bids, photo galleries and crew — one brutalist command center.</p>
        </div>
      </div>

      {/* Right / form */}
      <div className="flex items-center justify-center p-5 sm:p-10">
        <div className="w-full max-w-md animate-rise">
          <div className="flex justify-end mb-4"><LanguageToggle /></div>
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/sss-logo.png" alt="Swift Signings & Services LLC" className="w-11 h-11 border-2 border-black shadow-[3px_3px_0px_#000] object-cover" />
            <div>
              <div className="font-head font-black text-white uppercase text-lg leading-none">Swift Signings</div>
              <div className="font-mono text-[10px] text-[#FF5722] uppercase tracking-widest">& Services LLC</div>
            </div>
          </div>

          {needPicker ? (
            <Card className="p-6 sm:p-8" data-testid="role-picker">
              <h2 className="font-head font-black uppercase text-2xl text-white tracking-tight">{mode === "register" ? "Create account" : "Sign in"}</h2>
              <p className="text-zinc-500 text-sm mb-6 font-mono">First, tell us who you are</p>
              <div className="space-y-3">
                {Object.entries(ROLES).map(([key, { label, Icon, desc }]) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`pick-role-${key}`}
                    onClick={() => { setRole(key); setRoleChosen(true); }}
                    className="group w-full flex items-center gap-4 p-4 border-2 border-zinc-800 bg-zinc-950 hover:border-[#FF5722] hover:shadow-[4px_4px_0px_#000] transition-all text-left"
                  >
                    <div className="w-11 h-11 shrink-0 border-2 border-black bg-[#FF5722] text-black flex items-center justify-center shadow-[2px_2px_0px_#000]"><Icon size={20} /></div>
                    <div className="min-w-0 flex-1">
                      <div className="font-head font-bold uppercase text-white tracking-wide">{label}</div>
                      <div className="text-zinc-500 text-xs mt-0.5">{desc}</div>
                    </div>
                    <ArrowRight size={18} className="text-zinc-600 group-hover:text-[#FF5722] transition-colors shrink-0" />
                  </button>
                ))}
              </div>
              <p className="mt-6 font-mono text-[11px] text-zinc-600 text-center">Admins: sign in as either — we recognize your account automatically.</p>
            </Card>
          ) : (
            <>
              <Card className="p-6 sm:p-8">
                {(mode === "login" || mode === "register") && (
                  <button type="button" data-testid="change-role-btn" onClick={() => setRoleChosen(false)}
                    className="mb-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-[#FF5722] hover:text-white transition-colors">
                    <RoleIcon size={13} /> {ROLES[role].label} <span className="text-zinc-600">· change</span>
                  </button>
                )}
                <h2 className="font-head font-black uppercase text-2xl text-white tracking-tight">
                  {mode === "login" && "Sign in"}
                  {mode === "register" && "Create account"}
                  {mode === "forgot" && "Reset password"}
                  {mode === "reset" && "Enter code"}
                </h2>
                <p className="text-zinc-500 text-sm mb-6 font-mono">
                  {mode === "login" && `${ROLES[role].label} access portal`}
                  {mode === "register" && `Join as a ${ROLES[role].label.toLowerCase()}`}
                  {mode === "forgot" && "We'll email you a 6-digit code"}
                  {mode === "reset" && "Check your email for the code"}
                </p>

                <form onSubmit={submit} className="space-y-4">
                  {mode === "register" && (
                    <div>
                      <Label>Full name</Label>
                      <Input data-testid="register-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" required />
                    </div>
                  )}

                  <div>
                    <Label>Email</Label>
                    <Input data-testid="login-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required />
                  </div>

                  {mode === "reset" && (
                    <div>
                      <Label>6-digit code</Label>
                      <Input data-testid="reset-code" value={form.code} onChange={set("code")} placeholder="123456" maxLength={6} required />
                    </div>
                  )}

                  {(mode === "login" || mode === "register" || mode === "reset") && (
                    <div>
                      <Label>{mode === "reset" ? "New password" : "Password"}</Label>
                      <Input data-testid="login-password" type="password" value={form.password} onChange={set("password")} placeholder="••••••••" required />
                      {(mode === "register" || mode === "reset") && (
                        <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1" data-testid="password-rules">
                          {pwChecks.map(([label, ok]) => (
                            <li key={label} className={`flex items-center gap-1.5 font-mono text-[11px] ${ok ? "text-emerald-400" : "text-zinc-500"}`}>
                              {ok ? <Check size={12} /> : <X size={12} />} {label}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  <Btn data-testid="auth-submit-btn" type="submit" disabled={loading || ((mode === "register" || mode === "reset") && !pwStrong)} className="w-full">
                    {loading ? "…" : mode === "login" ? "Sign in" : mode === "register" ? "Create account" : mode === "forgot" ? "Send code" : "Reset password"}
                  </Btn>
                </form>

                {(mode === "login" || mode === "register") && (
                  <>
                    <div className="flex items-center gap-3 my-5">
                      <div className="h-0.5 bg-zinc-800 flex-1" />
                      <span className="font-mono text-xs text-zinc-600 uppercase">or</span>
                      <div className="h-0.5 bg-zinc-800 flex-1" />
                    </div>
                    <Btn variant="secondary" data-testid="google-signin-btn" onClick={google} className="w-full">
                      <Mail size={16} /> Continue with Google as {ROLES[role].label}
                    </Btn>
                  </>
                )}

                <div className="mt-6 text-sm text-zinc-500 space-y-2">
                  {mode === "login" && (
                    <>
                      <button data-testid="goto-register" onClick={() => setMode("register")} className="hover:text-[#FF5722] transition-colors block">No account? <span className="text-white font-semibold">Sign up</span></button>
                      <button data-testid="goto-forgot" onClick={() => setMode("forgot")} className="hover:text-[#FF5722] transition-colors block">Forgot password?</button>
                    </>
                  )}
                  {mode !== "login" && (
                    <button data-testid="goto-login" onClick={() => setMode("login")} className="flex items-center gap-1 hover:text-[#FF5722] transition-colors"><ArrowLeft size={14} /> Back to sign in</button>
                  )}
                </div>
              </Card>

              {mode === "login" && (
                <Card className="mt-4 p-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Demo logins (tap to fill)</div>
                  <div className="flex flex-wrap gap-2">
                    <button data-testid="demo-client" onClick={() => fillDemo("client.demo@example.com", "Client#2026")} className="text-xs font-mono bg-zinc-950 border border-zinc-800 hover:border-[#FF5722] px-2 py-1 text-zinc-300">Client</button>
                    <button data-testid="demo-crew" onClick={() => fillDemo("crew.demo@example.com", "Crew#2026")} className="text-xs font-mono bg-zinc-950 border border-zinc-800 hover:border-[#FF5722] px-2 py-1 text-zinc-300">Contractor</button>
                  </div>
                </Card>
              )}
            </>
          )}

          <a href="/request" data-testid="public-request-link" className="block mt-4 text-center font-head font-bold uppercase text-sm tracking-wide text-[#FF5722] hover:text-[#F4511E] border-2 border-[#FF5722]/40 hover:border-[#FF5722] py-3 transition-colors">Need service? Request without an account →</a>
          <button type="button" data-testid="how-it-works-btn" onClick={() => setHowOpen(true)} className="w-full mt-3 flex items-center justify-center gap-1.5 text-zinc-400 hover:text-[#FF5722] font-mono text-xs uppercase tracking-widest transition-colors"><HelpCircle size={14} /> New here? See how it works</button>
        </div>
      </div>
      <HowItWorksModal open={howOpen} onClose={() => setHowOpen(false)} />
    </div>
  );
}
